import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectContent,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageSquare,
} from "lucide-react";
import { handleWhatsAppRedirect } from "../helper/whatsapp";

// ─── Container ────────────────────────────────────────────────────────────────
const Container = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

// ─── Google Map ───────────────────────────────────────────────────────────────
const InteractiveMap = () => (
  <section className="py-0">
    <div className="w-full h-80 sm:h-96">
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3738.123456789!2d72.9!3d20.37!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sVapi%2C+Gujarat!5e0!3m2!1sen!2sin"
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        title="SS Plastic Manufacturing Location - Vapi, Gujarat"
      />
    </div>
  </section>
);

// ─── Product options ──────────────────────────────────────────────────────────
const PRODUCTS = [
  "Storage Tanks",
  "Pipes & Fittings",
  "FRP Ductings",
  "Scrubbers & Stack",
  "Industrial Vessels",
  "Motor Cover / Gutter",
  "Custom Fabrication",
];

const PURPOSES = [
  "New Project Requirement",
  "Replacement / Repair",
  "Get a Quote",
  "Technical Consultation",
  "Other",
];

// ─── Types ────────────────────────────────────────────────────────────────────
interface FormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  description: string;
}

interface FormErrors {
  [key: string]: string;
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    description: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleProductToggle = (product: string) => {
    setFormData((prev) => {
      const selected = prev.selectedProducts.includes(product)
        ? prev.selectedProducts.filter((p) => p !== product)
        : [...prev.selectedProducts, product];
      return { ...prev, selectedProducts: selected };
    });
    setErrors((prev) => ({ ...prev, selectedProducts: "" }));
  };

  const handleConsentChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, consent: checked }));
    setErrors((prev) => ({ ...prev, consent: "" }));
  };

  const validateForm = (): FormErrors => {
    const errs: FormErrors = {};
    if (!formData.customerName || formData.customerName.length < 2)
      errs.customerName = "Name must be at least 2 characters";
    if (!formData.customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail))
      errs.customerEmail = "Valid email is required";
    if (!formData.customerPhone || !/^\+?[\d\s()-]{10,}$/.test(formData.customerPhone))
      errs.customerPhone = "Valid phone number is required";
    if (!formData.description || formData.description.length < 10)
      errs.description = "Message must be at least 10 characters";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    // Send email directly
    const emailContent = `New Contact Form Submission from SS Plastic Website

Name: ${formData.customerName}
Email: ${formData.customerEmail}
Phone: ${formData.customerPhone}
Message: ${formData.description}

---
This email was sent from the SS Plastic contact form.`;

    // Create mailto link
    const mailtoLink = `mailto:info@ss-plastic.com?subject=New Contact Form Submission - ${formData.customerName}&body=${encodeURIComponent(emailContent)}`;
    
    // Open email client
    window.location.href = mailtoLink;

    setShowSuccess(true);
    setIsSubmitting(false);

    // Reset form
    setFormData({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      description: "",
    });

    setTimeout(() => setShowSuccess(false), 6000);
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | SS Plastic – FRP Manufacturer, Vapi Gujarat</title>
        <meta
          name="description"
          content="Contact SS Plastic for FRP tanks, scrubbers, pipes, ductings and custom fabrication. Call +91 98258 50501 or email info@ss-plastic.com. Vapi, Gujarat."
        />
        <meta property="og:title" content="Contact | SS Plastic" />
        <meta property="og:description" content="Contact SS Plastic for FRP tanks, scrubbers, pipes, ductings and custom fabrication. Call +91 98258 50501 or email info@ss-plastic.com. Vapi, Gujarat." />
        <meta property="og:image" content="/ssplasticlogo.webp" />
        <meta property="og:url" content="https://www.ss-plastic.com/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact | SS Plastic" />
        <meta name="twitter:description" content="Contact SS Plastic for FRP tanks, scrubbers, pipes, ductings and custom fabrication. Call +91 98258 50501 or email info@ss-plastic.com. Vapi, Gujarat." />
        <meta name="twitter:image" content="/ssplasticlogo.webp" />
        <link rel="canonical" href="https://www.ss-plastic.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "SS Plastic",
            url: "https://www.ss-plastic.com",
            contactPoint: [
              {
                "@type": "ContactPoint",
                telephone: "+91-9825850501",
                contactType: "Sales",
                availableLanguage: ["English", "Hindi", "Gujarati"],
                areaServed: "IN",
              },
              {
                "@type": "ContactPoint",
                telephone: "+91-9879950501",
                contactType: "Technical Support",
              },
            ],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Vapi",
              addressRegion: "Gujarat",
              postalCode: "396195",
              addressCountry: "IN",
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* ── Hero ── */}
        <section className="bg-gradient-to-br from-egyptian-blue to-violet-blue text-white py-14 sm:py-20">
          <Container>
            <div className="text-center">
              <h1 className="font-display font-bold text-3xl sm:text-5xl mb-4">
                Contact Us
              </h1>
              <p className="text-base sm:text-lg max-w-2xl mx-auto text-white/90">
                Get in touch with our team for FRP product inquiries, custom
                fabrication quotes, or technical consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <a
                  href="tel:+919825850501"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-5 py-2 text-sm transition-colors"
                >
                  <Phone className="h-4 w-4" /> +91 98258 50501
                </a>
                <a
                  href="mailto:info@ss-plastic.com"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-5 py-2 text-sm transition-colors"
                >
                  <Mail className="h-4 w-4" /> info@ss-plastic.com
                </a>
              </div>
            </div>
          </Container>
        </section>

        {/* ── Form + Sidebar ── */}
        <section className="py-10 sm:py-16 bg-white">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Left - Form */}
              <div className="lg:col-span-2">
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-egyptian-blue text-xl sm:text-2xl font-display">
                      Get In Touch
                    </CardTitle>
                    <p className="text-gray-600 text-sm">
                      Send us a message and we'll respond as soon as possible
                    </p>
                  </CardHeader>
                  <CardContent className="p-5 sm:p-6 space-y-5 bg-gray-50 rounded-b-lg">
                    {/* Success */}
                    {showSuccess && (
                      <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-300 rounded-lg text-green-800">
                        <CheckCircle className="h-5 w-5 shrink-0" />
                        <span className="text-sm font-medium">
                          Inquiry sent successfully! We'll respond within 24
                          hours.
                        </span>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Your Name */}
                      <div className="space-y-1.5">
                        <Label htmlFor="customerName" className="text-sm font-medium">
                          Your Name *
                        </Label>
                        <Input
                          id="customerName"
                          value={formData.customerName}
                          onChange={handleInputChange}
                          placeholder="Your Name"
                          disabled={isSubmitting}
                        />
                        {errors.customerName && (
                          <p className="text-red-600 text-xs">
                            {errors.customerName}
                          </p>
                        )}
                      </div>

                      {/* Your Email */}
                      <div className="space-y-1.5">
                        <Label htmlFor="customerEmail" className="text-sm font-medium">
                          Your Email *
                        </Label>
                        <Input
                          id="customerEmail"
                          type="email"
                          value={formData.customerEmail}
                          onChange={handleInputChange}
                          placeholder="Your Email"
                          disabled={isSubmitting}
                        />
                        {errors.customerEmail && (
                          <p className="text-red-600 text-xs">
                            {errors.customerEmail}
                          </p>
                        )}
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-1.5">
                        <Label htmlFor="customerPhone" className="text-sm font-medium">
                          Phone Number *
                        </Label>
                        <Input
                          id="customerPhone"
                          value={formData.customerPhone}
                          onChange={handleInputChange}
                          placeholder="Phone Number"
                          disabled={isSubmitting}
                        />
                        {errors.customerPhone && (
                          <p className="text-red-600 text-xs">
                            {errors.customerPhone}
                          </p>
                        )}
                      </div>

                      {/* Your Message */}
                      <div className="space-y-1.5">
                        <Label htmlFor="description" className="text-sm font-medium">
                          Your Message *
                        </Label>
                        <textarea
                          id="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          placeholder="Your Message"
                          className="w-full p-3 border rounded-lg text-sm focus:ring-2 focus:ring-egyptian-blue focus:border-transparent bg-white"
                          rows={4}
                          disabled={isSubmitting}
                        />
                        {errors.description && (
                          <p className="text-red-600 text-xs">
                            {errors.description}
                          </p>
                        )}
                      </div>

                      {/* Consent */}
                      <div className="space-y-1.5">
                        <div className="flex items-start gap-2">
                          <Checkbox
                            id="consent"
                            checked={formData.consent}
                            onCheckedChange={handleConsentChange}
                            disabled={isSubmitting}
                            className="mt-0.5"
                          />
                          <Label htmlFor="consent" className="text-sm text-gray-600">
                            I agree to share my details for this inquiry *
                          </Label>
                        </div>
                        {errors.consent && (
                          <p className="text-red-600 text-xs">
                            {errors.consent}
                          </p>
                        )}
                      </div>

                      {/* Submit */}
                      <div className="pt-2">
                        <Button
                          type="submit"
                          variant="cta"
                          size="lg"
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send Message"}
                        </Button>
                      </div>

                      <div className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        Typical response: 4-6 hours during business days
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Right - Sidebar */}
              <div className="space-y-5">
                {/* Contact Info */}
                <Card className="shadow-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-egyptian-blue flex items-center gap-2 text-base">
                      <Phone className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <a
                      href="tel:+919825850501"
                      className="flex items-start gap-3 group"
                    >
                      <Phone className="h-4 w-4 text-amber mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-sm group-hover:text-egyptian-blue transition-colors">
                          Sales Hotline
                        </div>
                        <div className="text-gray-600 text-sm">
                          +91 98258 50501
                        </div>
                        <div className="text-xs text-gray-400">
                          Mon-Sat, 9 AM - 6 PM
                        </div>
                      </div>
                    </a>

                    <a
                      href="tel:+919825850501"
                      className="flex items-start gap-3 group"
                    >
                      <Phone className="h-4 w-4 text-amber mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-sm group-hover:text-egyptian-blue transition-colors">
                          Alternate Number
                        </div>
                        <div className="text-gray-600 text-sm">
                          +91 98258 50501
                        </div>
                      </div>
                    </a>

                    <a
                      href="mailto:info@ss-plastic.com"
                      className="flex items-start gap-3 group"
                    >
                      <Mail className="h-4 w-4 text-amber mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-sm group-hover:text-egyptian-blue transition-colors">
                          Email
                        </div>
                        <div className="text-gray-600 text-sm">
                          info@ss-plastic.com
                        </div>
                        <div className="text-xs text-gray-400">
                          Response within 4 hours
                        </div>
                      </div>
                    </a>

                    <div
                      className="flex items-start gap-3 cursor-pointer group"
                      onClick={handleWhatsAppRedirect}
                    >
                      <MessageSquare className="h-4 w-4 text-amber mt-1 shrink-0" />
                      <div>
                        <div className="font-semibold text-sm group-hover:text-egyptian-blue transition-colors">
                          WhatsApp
                        </div>
                        <div className="text-gray-600 text-sm">
                          +91 98258 50501
                        </div>
                        <div className="text-xs text-gray-400">
                          Quick queries & quotes
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Office Address */}
                <Card className="shadow-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-egyptian-blue flex items-center gap-2 text-base">
                      <MapPin className="h-5 w-5" />
                      Manufacturing Unit
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="font-semibold text-sm">SS Plastic</div>
                        <div className="text-gray-600 text-sm mt-1">
                          Vapi, Gujarat, India
                          <br />
                          Pin: 396195
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                        <Badge
                          variant="outline"
                          className="text-xs text-amber border-amber"
                        >
                          ISO 9001:2015
                        </Badge>
                        <Badge
                          variant="outline"
                          className="text-xs text-gray-500"
                        >
                          GST: 24BIRPS4800G2Z3
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* CTA Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="cta"
                    size="lg"
                    className="w-full"
                    onClick={handleWhatsAppRedirect}
                  >
                    <svg
                      style={{ width: "18px", height: "18px" }}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 128 128"
                      className="mr-2"
                    >
                      <path
                        fill="white"
                        d="M 64 2 C 29.8 2 2 29.8 2 64 C 2 74.5 4.6 84.8 9.7 93.9 L 4.4 113.3 C 3.5 116.4 4.4 119.6 6.7 121.9 C 9 124.2 12.2 125.1 15.3 124.3 L 35.5 119 C 44.3 123.6 54.1 126 64.1 126 C 98.3 126 126.1 98.2 126.1 64 C 126.1 47.4 119.7 31.9 108 20.2 C 96.2 8.5 80.6 2 64 2 z"
                      />
                    </svg>
                    WhatsApp Quick Quote
                  </Button>

                  {/* <Button variant="enterprise" size="lg" className="w-full">
                    Request Product Samples
                  </Button> */}

                  <Button variant="trust" size="lg" className="w-full" asChild>
                    <a href="/SS PLASTIC.pdf" download="SS-Plastic-Company-Profile.pdf">
                      Download Company Profile
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ── Map ── */}
        <InteractiveMap />

        {/* ── Floating WhatsApp ── */}
        <div className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-50">
          <Button
            size="sm"
            className="shadow-xl bg-green-600 hover:bg-green-700"
            onClick={handleWhatsAppRedirect}
          >
            <svg
              style={{ width: "20px", height: "20px" }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
            >
              <path
                fill="white"
                d="M 64 2 C 29.8 2 2 29.8 2 64 C 2 74.5 4.6 84.8 9.7 93.9 L 4.4 113.3 C 3.5 116.4 4.4 119.6 6.7 121.9 C 9 124.2 12.2 125.1 15.3 124.3 L 35.5 119 C 44.3 123.6 54.1 126 64.1 126 C 98.3 126 126.1 98.2 126.1 64 C 126.1 47.4 119.7 31.9 108 20.2 C 96.2 8.5 80.6 2 64 2 z"
              />
            </svg>
            <span className="ml-2">Quick Quote</span>
          </Button>
        </div>
      </div>
    </>
  );
};

export default Contact;